import {Injectable} from '@angular/core';
import {UIArtefakt, UIArtefaktGruppe, UIMenu, UIMenuGruppe, UIMenuSeite} from '../models/uiArtefakt';
import {ErrorService, RuntimeContext} from '@lv1871/core';
import {UIArtefaktTyp} from '../models/uiArtefaktTyp';
import {EventService} from './event.service';
import {takeWhile, tap} from 'rxjs/operators';
import {ShellConfigService} from './shell-config.service';
import {UILayout} from '../models/uiLayout';
import {UISeitensortierungSBPortal, UiVorgang} from '../models/uiVorgang';

const assign = (target: object, ...sources: object[]) => {
  sources.forEach((source) => Object.keys(source).forEach((key) => {
    target[key] = source[key];
  }));
  return target;
};

export interface ArtefaktGruppenEntry {
  gruppe: UIArtefaktGruppe;
  baseGruppe: UIArtefaktGruppe;
}

@Injectable()
export class MenuService {

// for testing only
  private _menuStructureTest: UIMenu = null;

  constructor(private _errorService: ErrorService, private _eventService: EventService,
              private _shellConfigService: ShellConfigService) {
  }

  createMenu(uiVorgang: UiVorgang) {
    const uiArtefakt = uiVorgang.UIArtefakt;
    try {
      const menu = new UIMenu();
      menu.UIMenuGruppen = new Array<UIMenuGruppe>();
      let menuGruppeActual: UIMenuGruppe;
      let menuSeiteActual: UIMenuSeite;

      const uiArtefaktGruppeMap = new Map<string, UIArtefaktGruppe>();
      this.extractArtefaktGruppen(uiArtefakt).forEach(artefakt => {
        const uiArtefaktGruppe = assign(new UIArtefaktGruppe(), artefakt) as UIArtefaktGruppe;
        uiArtefaktGruppeMap.set(uiArtefaktGruppe.UIArtefaktGruppe, uiArtefaktGruppe);
      });
      this.validateArtefaktGruppen(this.extractArtefaktGruppen(uiArtefakt), new Map<string, ArtefaktGruppenEntry>());

      let artefaktCounter = 0;
      uiArtefakt.forEach(artefakt => {
        switch (artefakt.UIArtefakttyp) {
          case UIArtefaktTyp.MENU_GRUPPE:
            if (!!menuSeiteActual && artefaktCounter === 0) {
              menuGruppeActual.UIMenuSeiten.pop();
            }
            menuGruppeActual = this.createMenuGruppe(menu, artefakt);
            menuSeiteActual = null;
            break;
          case UIArtefaktTyp.MENU_SEITE:
            if (artefaktCounter === 0) {
              menuGruppeActual.UIMenuSeiten.pop();
            }
            menuSeiteActual = this.createMenuSeite(menuGruppeActual, artefakt);
            artefaktCounter = 0;
            break;
          case UIArtefaktTyp.ARTEFAKT_GRUPPE:
            break;
          case UIArtefaktTyp.UI_CONTAINER:
            break;
          default:
            if (!!menuSeiteActual) {
              if(!!artefakt.UIArtefakt || artefakt.UIArtefakttyp !== UIArtefaktTyp.BLOCK) {
                artefaktCounter++;
                this._setMenuseiteInArtefakt(artefakt, menuSeiteActual.Identifikator);
                menuSeiteActual.UIArtefakt.push(artefakt);
              }
            } else {
              throw new Error(`Das UIArtefakt ${artefakt.Identifikator} liegt nicht in einer MenuSeite`);
            }
        }
      });
      if(artefaktCounter === 0){
        menuGruppeActual.UIMenuSeiten.pop();
      }

      if (this._shellConfigService.getParameter().runtimeContext === RuntimeContext.SB_SHELL && !!uiVorgang.UISeitensortierungSBPortal) {
        this._validateMenuPagesForSBShell(uiVorgang.UISeitensortierungSBPortal);
        this._reorderMenuPagesForSBShell(menu, uiVorgang.UISeitensortierungSBPortal);
      }

      if (!!menu && !!menu.UIMenuGruppen[0] && menu.UIMenuGruppen[0].UIMenuSeiten.length > 0) {
        this._eventService.menuItemSelected$.pipe(
            takeWhile(seite => !seite),
            tap(_ => {
              const uiLayout = this._shellConfigService.getParameter().uiLayout;
              if (uiLayout === UILayout.TAB_MENU_NACH_GRUPPEN || uiLayout === UILayout.AKKORDEON_MENU_NACH_GRUPPEN) {
                if (!!menu.UIMenuGruppen[0].Identifikator) {
                  // im Fisalis ist der Identifikator für MenuGruppen im UISeitenSortierungSBPortal nicht gesetzt, das
                  // würde zu einem endless-loop führen
                  this._eventService.menuItemSelected$.next(menu.UIMenuGruppen[0].Identifikator);
                }
              } else {
                this._eventService.menuItemSelected$.next(menu.UIMenuGruppen[0].UIMenuSeiten[0].Identifikator);
              }
            }))
        .subscribe();
      }

      this._eventService.uiArtefaktGruppen$.next(uiArtefaktGruppeMap);
      this._eventService.menu$.next(menu);
      this._menuStructureTest = menu;
    } catch (e) {
      this._errorService.pushErrorMessage(e.message);
    }
  }

  extractArtefaktGruppen(uiArtefakt: UIArtefakt[]): UIArtefakt[] {
    return uiArtefakt.reduce((previousValue: UIArtefakt[], currentValue: UIArtefakt) => {
          if (currentValue.UIArtefakttyp === UIArtefaktTyp.ARTEFAKT_GRUPPE) {
            previousValue.push(currentValue);
          } else if (!!currentValue.UIArtefakt) {
            previousValue.push(...this.extractArtefaktGruppen(currentValue.UIArtefakt));
          }
          return previousValue;
        },
        [] as UIArtefakt[]);
  }

  validateArtefaktGruppen(uiArtefakt: UIArtefakt[], uiArtefaktGruppeMap: Map<string, ArtefaktGruppenEntry>, baseGruppe?: UIArtefaktGruppe) {
    uiArtefakt.forEach(artefakt => {
      if (artefakt.UIArtefakttyp !== UIArtefaktTyp.ARTEFAKT_GRUPPE) {
        this._errorService.pushErrorMessage(`Das Artefakt ${artefakt.Identifikator} wurde in eine UIArtefaktGruppe geschachtelt!`);
      } else {
        const gruppe = artefakt as UIArtefaktGruppe;
        if (!!uiArtefaktGruppeMap.get(gruppe.UIArtefaktGruppe)) {
          if (gruppe.Identifikator === uiArtefaktGruppeMap.get(gruppe.UIArtefaktGruppe).gruppe.Identifikator) {
            if ((baseGruppe ? baseGruppe.Identifikator : gruppe.Identifikator)
                !== uiArtefaktGruppeMap.get(gruppe.UIArtefaktGruppe).baseGruppe.Identifikator) {
              this._errorService.pushErrorMessage('Das UIArtefaktGruppen-Artefakt ' + gruppe.Identifikator
                  + ' wurde an mehreren Stellen in unterschiedlicher Schachtelung verwendet!');
            }
          } else {
            this._errorService.pushErrorMessage('Das Attribut UIArtefaktGruppe=' + gruppe.UIArtefaktGruppe
                + ' wurde in mehreren UIArtefaktGruppen-Artefakten gesetzt: ' + uiArtefaktGruppeMap.get(gruppe.UIArtefaktGruppe).gruppe.Identifikator
                + ' und ' + gruppe.Identifikator);
          }
        }
        uiArtefaktGruppeMap.set(gruppe.UIArtefaktGruppe, {
          gruppe,
          baseGruppe: (baseGruppe ? baseGruppe : gruppe)
        });
        if (!!gruppe.UIArtefakt) {
          this.validateArtefaktGruppen(gruppe.UIArtefakt, uiArtefaktGruppeMap, gruppe);
        }
      }
    });
  }

  private createMenuGruppe(menu: UIMenu, artefakt: UIArtefakt): UIMenuGruppe {
    const menuGruppe = assign(new UIMenuGruppe(), artefakt) as UIMenuGruppe;
    menuGruppe.UIMenuSeiten = new Array<UIMenuSeite>();
    menu.UIMenuGruppen.push(menuGruppe);
    return menuGruppe;
  }

  private createMenuSeite(menuGruppe: UIMenuGruppe, artefakt: UIArtefakt): UIMenuSeite {
    if (!menuGruppe) {
      throw new Error(`MenuSeite ${artefakt.Identifikator} liegt nicht in einer MenuGruppe`);
    }
    const menuSeite = assign(new UIMenuSeite(), artefakt) as UIMenuSeite;
    menuSeite.UIArtefakt = [];
    menuGruppe.UIMenuSeiten.push(menuSeite);
    return menuSeite;
  }

  private _setMenuseiteInArtefakt(artefakt: UIArtefakt, menuSeiteActual: string) {
    artefakt.menuseiteForValidationOnly = menuSeiteActual;
    if (!!artefakt.UIArtefakt && !!artefakt.UIArtefakt.length) {
      artefakt.UIArtefakt.forEach(artef => this._setMenuseiteInArtefakt(artef, menuSeiteActual));
    }
  }

  private _validateMenuPagesForSBShell(seitensortierung: UISeitensortierungSBPortal[]) {
    seitensortierung.filter(gruppe => !!gruppe.SeitenID).forEach(gruppe => {
      this._errorService.pushWarningMessage('Modellierungswarnung UISeitensortierungSBPortal: Eine Gruppe ' + gruppe.MenuTitel
          + ' sollte keine SeitenID haben. Folgende ist gesetzt: ' + gruppe.SeitenID);
    });
    seitensortierung.forEach(gruppe => {
      if (!gruppe.UISeitensortierungSBPortal) {
        this._errorService.pushWarningMessage('Modellierungswarnung UISeitensortierungSBPortal: Die Gruppe ' + gruppe.MenuTitel
        + ' hat keine eingehängten Seiten als Kindelemente');
      }
      gruppe.UISeitensortierungSBPortal?.forEach(seite => {
        if (!seite.SeitenID) {
          this._errorService.pushErrorMessage('Modellierungsfehler UISeitensortierungSBPortal: Es gibt in der Gruppe ' + gruppe.MenuTitel
              + ' eine Seite ohne SeitenID');
        }
        if (!!seite.UISeitensortierungSBPortal?.length) {
          this._errorService.pushErrorMessage('Modellierungsfehler UISeitensortierungSBPortal: Die Seite mit SeitenID ' + seite.SeitenID
              + ' enthält weitere Seiten als Kindelemente');
        }
      });
    });
  }

  private _reorderMenuPagesForSBShell(menu: UIMenu, seitensortierung: UISeitensortierungSBPortal[]) {
    seitensortierung.forEach(gruppe => {
      const menuGruppe = new UIMenuGruppe();
      menuGruppe.UIMenuSeiten = new Array<UIMenuSeite>();
      menuGruppe.MenuTitel = gruppe.MenuTitel;
      menuGruppe.MenuSeitenAusgeblendetJN = gruppe.MenuSeitenAusgeblendetJN;
      menu.UIMenuGruppen.unshift(menuGruppe);

      gruppe.UISeitensortierungSBPortal.forEach(seite => {
        const menuSeite = this._extractMenuSeite(menu, seite.SeitenID);
        if (!!menuSeite) {
          menuGruppe.UIMenuSeiten.push(menuSeite);
        }
      });
    });

    menu.UIMenuGruppen = menu.UIMenuGruppen.filter(gruppe => !!gruppe.UIMenuSeiten.length && !gruppe.MenuSeitenAusgeblendetJN);
  }

  private _extractMenuSeite(menu: UIMenu, seitenId: string): UIMenuSeite {
    for (const gruppe of menu.UIMenuGruppen) {
      for (let i = 0; i < gruppe.UIMenuSeiten.length; i++) {
        if (gruppe.UIMenuSeiten[i].SeitenID === seitenId) {
          const menuSeite = gruppe.UIMenuSeiten[i];
          gruppe.UIMenuSeiten.splice(i, 1);
          return menuSeite;
        }
      }
    }
    return null;
  }
}
